const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Thrown when a date string is not a valid `YYYY-MM-DD` calendar date. */
export class RecurrenceDateError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RecurrenceDateError';
    }
}
function parse(dateIso) {
    if (!ISO_DATE_RE.test(dateIso)) {
        throw new RecurrenceDateError(`Invalid ISO date: "${dateIso}" (expected YYYY-MM-DD)`);
    }
    const y = Number(dateIso.slice(0, 4));
    const m = Number(dateIso.slice(5, 7));
    const d = Number(dateIso.slice(8, 10));
    if (m < 1 || m > 12 || d < 1 || d > 31) {
        throw new RecurrenceDateError(`Out-of-range ISO date: "${dateIso}"`);
    }
    return { y, m, d };
}
/** Days since the Unix epoch for a calendar date (UTC midnight — DST-safe). */
function dayNumber(dateIso) {
    const { y, m, d } = parse(dateIso);
    return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}
/** 0 = Sunday … 6 = Saturday for the given calendar date. */
function weekdayOf(dateIso) {
    const { y, m, d } = parse(dateIso);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
/** Convert a day-number back to an ISO `YYYY-MM-DD` string. */
function isoFromDayNumber(dayNum) {
    const date = new Date(dayNum * 86_400_000);
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth() + 1;
    const d = date.getUTCDate();
    const pad = (n) => String(n).padStart(2, '0');
    return `${y}-${pad(m)}-${pad(d)}`;
}
/** Does `recurrence` fire on the calendar day `dateIso`? */
export function recurrenceDueOn(recurrence, dateIso) {
    // Validate the query date up-front so every variant rejects malformed
    // input consistently (not just the variants that do day math).
    parse(dateIso);
    switch (recurrence.kind) {
        case 'once':
            return recurrence.date === dateIso;
        case 'daily':
            return true;
        case 'weekly':
            return recurrence.days.includes(weekdayOf(dateIso));
        case 'everyNDays': {
            if (recurrence.n <= 0)
                return false;
            const delta = dayNumber(dateIso) - dayNumber(recurrence.anchor);
            return delta >= 0 && delta % recurrence.n === 0;
        }
        case 'monthly': {
            const { d } = parse(dateIso);
            return d === recurrence.day;
        }
    }
}
/** Convenience: does a full `CalendarEvent` fire on `dateIso`? */
export function isDueOn(event, dateIso) {
    return recurrenceDueOn(event.recurrence, dateIso);
}
/**
 * Enumerate every calendar date in the inclusive window `[fromIso, toIso]`
 * on which `event` is due, in ascending order. Returns `[]` when `to < from`.
 */
export function occurrencesBetween(event, fromIso, toIso) {
    const fromDay = dayNumber(fromIso);
    const toDay = dayNumber(toIso);
    const out = [];
    for (let day = fromDay; day <= toDay; day += 1) {
        const dateIso = isoFromDayNumber(day);
        if (recurrenceDueOn(event.recurrence, dateIso)) {
            out.push(dateIso);
        }
    }
    return out;
}
/** Human-readable summary of a recurrence (for list rows / tiles). */
export function describeRecurrence(recurrence) {
    switch (recurrence.kind) {
        case 'once':
            return `Once on ${recurrence.date}`;
        case 'daily':
            return 'Every day';
        case 'weekly': {
            const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const labels = [...recurrence.days].sort((a, b) => a - b).map((d) => names[d]);
            return labels.length === 7 ? 'Every day' : `Weekly: ${labels.join(', ')}`;
        }
        case 'everyNDays':
            return recurrence.n === 1 ? 'Every day' : `Every ${recurrence.n} days`;
        case 'monthly': {
            const suffix = recurrence.day === 1 || recurrence.day === 21 || recurrence.day === 31
                ? 'st'
                : recurrence.day === 2 || recurrence.day === 22
                    ? 'nd'
                    : recurrence.day === 3 || recurrence.day === 23
                        ? 'rd'
                        : 'th';
            return `Monthly on the ${recurrence.day}${suffix}`;
        }
    }
}
//# sourceMappingURL=recurrence.js.map